import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { tripsApi, vehiclesApi } from '@/api/endpoints'
import type { OdometerHint, PerKmRate, Trip, TripSuggestions, Vehicle, VehicleOwnership } from '@/api/types'
import { Button, Card, Field, Input, Select, Textarea } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { errorMessage } from '@/utils/errors'
import { formatBytes, formatCzk, formatDate, formatDateTime, formatKm, formatNumber, formatPlate } from '@/utils/format'

interface Point {
  place: string
  km: string
}

const EMPTY_SUGGESTIONS: TripSuggestions = { places: [], distances: [], drivers: [] }

/** Zápis jízdy (`/cesty/nova`) i její úprava: trasa po bodech s km mezi nimi. */
export default function TripFormPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'nova'
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [suggestions, setSuggestions] = useState<TripSuggestions>(EMPTY_SUGGESTIONS)
  const [trip, setTrip] = useState<Trip | null>(null)
  const [vehicleId, setVehicleId] = useState(params.get('auto') ?? '')
  const [tripDate, setTripDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [driverName, setDriverName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [note, setNote] = useState('')
  /** Ruční tachometr na startu; prázdný = navázat na předchozí jízdu. */
  const [manualStart, setManualStart] = useState('')
  const [hint, setHint] = useState<OdometerHint | null>(null)
  /** Cena PHM z dokladu o tankování; prázdná = cena z vyhlášky. */
  const [fuelPrice, setFuelPrice] = useState('')
  /** Doklady vybrané k nahrání — nahrají se při uložení jízdy. */
  const [pending, setPending] = useState<File[]>([])
  const [confirmFile, setConfirmFile] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const [points, setPoints] = useState<Point[]>([
    { place: '', km: '' },
    { place: '', km: '' },
  ])
  const [busy, setBusy] = useState(false)
  const [driverTouched, setDriverTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [saved, setSaved] = useState<string[]>([])
  // „Zapsat a další“: po uložení zůstaň ve formuláři a posuň datum o den.
  const keepGoing = useRef(false)

  useEffect(() => {
    vehiclesApi.list().then(setVehicles).catch(() => setVehicles([]))
    tripsApi.suggestions().then(setSuggestions).catch(() => setSuggestions(EMPTY_SUGGESTIONS))
  }, [])

  useEffect(() => {
    if (isNew || !id) return
    tripsApi
      .detail(id)
      .then((t) => {
        setTrip(t)
        setVehicleId(t.vehicleId)
        setTripDate(t.tripDate)
        setDriverName(t.driverName)
        setPurpose(t.purpose)
        setNote(t.note ?? '')
        setManualStart(t.odometerManual && t.odometerStart !== null ? String(t.odometerStart) : '')
        setFuelPrice(t.fuelPrice !== null ? String(t.fuelPrice).replace('.', ',') : '')
        setPoints(t.points.map((p, i) => ({ place: p.place, km: i === 0 ? '' : String(p.km) })))
      })
      .catch((e) => setError(errorMessage(e, 'Jízdu se nepodařilo načíst.')))
  }, [id, isNew])

  const vehicle = vehicles.find((v) => v.id === vehicleId)
  const selectable = vehicles.filter((v) => v.active || v.id === vehicleId)

  // Nová jízda: jediné aktivní auto vyber rovnou; řidiče předvyplň vlastníkem
  // soukromého auta, jinak přihlášeným uživatelem.
  useEffect(() => {
    if (!isNew) return
    if (!vehicleId && selectable.length === 1) setVehicleId(selectable[0].id)
  }, [isNew, vehicleId, selectable])

  // Řidiče doplníme, až je vybrané auto, a jen dokud ho nikdo nepřepsal.
  useEffect(() => {
    if (!isNew || driverTouched || !vehicle) return
    if (vehicle.ownership === 'EMPLOYEE' && vehicle.ownerName) setDriverName(vehicle.ownerName)
    else if (user) setDriverName([user.firstName, user.lastName].filter(Boolean).join(' ') || user.email)
  }, [isNew, vehicle, user, driverTouched])

  /** Naposledy zadaná vzdálenost mezi dvěma místy, v libovolném směru. */
  const knownKm = (from: string, to: string): number | undefined => {
    const a = from.trim().toLowerCase()
    const b = to.trim().toLowerCase()
    if (!a || !b) return undefined
    return suggestions.distances.find(
      (d) =>
        (d.from.toLowerCase() === a && d.to.toLowerCase() === b) ||
        (d.from.toLowerCase() === b && d.to.toLowerCase() === a),
    )?.km
  }

  const setPoint = (i: number, patch: Partial<Point>) =>
    setPoints((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)))

  /** Po vyplnění místa doplň km, pokud tenhle úsek už někdo jel. */
  const fillKm = (i: number) => {
    if (i === 0) return
    setPoints((ps) => {
      const km = knownKm(ps[i - 1].place, ps[i].place)
      if (km === undefined || ps[i].km !== '') return ps
      return ps.map((p, j) => (j === i ? { ...p, km: String(km) } : p))
    })
  }

  const addPoint = (place = '') => {
    const last = points[points.length - 1]
    const km = place ? knownKm(last.place, place) : undefined
    setPoints([...points, { place, km: km !== undefined ? String(km) : '' }])
  }

  const removePoint = (i: number) => setPoints(points.filter((_, j) => j !== i))

  const kmValues = points.slice(1).map((p) => Number(p.km.replace(',', '.')))
  const totalKm = kmValues.every((k) => Number.isFinite(k)) ? kmValues.reduce((a, b) => a + b, 0) : null
  const start = points[0].place.trim()
  const last = points[points.length - 1].place.trim()

  // Kde skončila předchozí jízda auta k tomuhle dni — na to jízda naváže,
  // pokud se tachometr nezadá ručně.
  useEffect(() => {
    if (!vehicleId || !tripDate) return setHint(null)
    tripsApi
      .odometer({ vehicleId, date: tripDate, tripId: isNew ? undefined : id })
      .then(setHint)
      .catch(() => setHint(null))
  }, [vehicleId, tripDate, isNew, id, saved.length])

  const manualValue = manualStart.trim() === '' ? null : Number(manualStart.replace(/\s/g, ''))
  const odometerStart = manualValue ?? hint?.expectedStart ?? null
  const odometerGap =
    manualValue !== null && Number.isFinite(manualValue) && hint?.expectedStart != null
      ? manualValue - hint.expectedStart
      : 0

  // Sazby: u uložené jízdy k jejímu dni, u nové (nebo po změně auta) dnešní.
  const savedRates = trip !== null && trip.vehicleId === vehicleId
  const rates = savedRates ? trip.rate : (vehicle?.rateToday ?? null)
  const defaultFuelPrice = savedRates ? trip.defaultFuelPrice : (vehicle?.rateToday.fuelPrice ?? null)
  const fuelUnit = vehicle?.fuelUnit ?? trip?.fuelUnit ?? 'l'
  const fuelValue = fuelPrice.trim() === '' ? null : Number(fuelPrice.trim().replace(',', '.'))
  const fuelValid = fuelValue === null || (/^\d+([.,]\d{1,2})?$/.test(fuelPrice.trim()) && fuelValue > 0 && fuelValue < 1000)
  const priceUsed = fuelValid ? (fuelValue ?? defaultFuelPrice?.value ?? null) : null

  const perKm = useMemo(
    () => (rates && vehicle ? perKmWith(rates, vehicle.ownership, priceUsed) : null),
    [rates, vehicle, priceUsed],
  )
  const preview = perKm !== null && totalKm !== null ? Math.round(perKm * totalKm * 100) / 100 : null

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (points.slice(1).some((p) => !/^\d+$/.test(p.km.trim()) || Number(p.km) <= 0)) {
      setError('U každého dalšího bodu vyplň celé km od předchozího.')
      return
    }
    if (manualValue !== null && (!Number.isInteger(manualValue) || manualValue < 0)) {
      setError('Tachometr na startu musí být celé číslo.')
      return
    }
    if (!fuelValid) {
      setError(`Cena paliva musí být kladné číslo v Kč/${fuelUnit} (nejvýš 2 desetinná místa).`)
      return
    }
    setBusy(true)
    const body = {
      vehicleId,
      tripDate,
      driverName,
      purpose,
      note: note || null,
      odometerStart: manualValue,
      fuelPrice: fuelValue,
      points: points.map((p, i) => ({ place: p.place, km: i === 0 ? null : Number(p.km) })),
    }
    try {
      const result = isNew ? await tripsApi.create(body) : await tripsApi.update(id!, body)
      if (pending.length > 0) {
        try {
          await tripsApi.upload(result.id, pending)
        } catch (err) {
          // Jízda je uložená — k úpravě, ať jde doklad nahrát znovu.
          setError(errorMessage(err, 'Jízda je uložená, ale doklad se nepodařilo nahrát.'))
          setBusy(false)
          if (isNew) navigate(`/cesty/${result.id}`, { replace: true })
          return
        }
      }
      if (isNew && keepGoing.current) {
        setSaved((s) => [...s, tripDate])
        setTripDate(dayjs(tripDate).add(1, 'day').format('YYYY-MM-DD'))
        setManualStart('') // další jízda naváže na tuhle
        setFuelPrice('') // tankování patří jen k téhle jízdě
        setPending([])
        vehiclesApi.list().then(setVehicles).catch(() => undefined) // tachometr pro další jízdu
        setBusy(false)
        return
      }
      navigate('/cesty')
    } catch (err) {
      setError(errorMessage(err, 'Jízdu se nepodařilo uložit.'))
      setBusy(false)
    }
  }

  const removeFile = async (fileId: string) => {
    if (confirmFile !== fileId) return setConfirmFile(fileId)
    try {
      setTrip(await tripsApi.removeFile(fileId))
    } catch (err) {
      setError(errorMessage(err, 'Doklad se nepodařilo smazat.'))
    }
    setConfirmFile(null)
  }

  const remove = async () => {
    if (!confirmDelete) return setConfirmDelete(true)
    try {
      await tripsApi.remove(id!)
      navigate('/cesty')
    } catch (err) {
      setError(errorMessage(err, 'Jízdu se nepodařilo smazat.'))
    }
  }

  if (!isNew && !trip) {
    return error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-slate-400">Načítání…</p>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/cesty" className="text-sm text-slate-500 hover:text-slate-700">
          ← Kniha jízd
        </Link>
        <h1 className="text-lg font-semibold">
          {isNew ? 'Nová jízda' : `Jízda ${formatDate(trip!.tripDate)}`}
        </h1>
      </div>

      {!isNew || vehicles.length > 0 ? null : (
        <Card className="text-sm text-slate-500">
          Nejdřív přidej auto ve{' '}
          <Link to="/vozidla" className="text-brand-600 hover:underline">
            vozovém parku
          </Link>
          .
        </Card>
      )}

      <form onSubmit={save} className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            <Field label="Auto">
              <Select required value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">— vyber —</option>
                {selectable.map((v) => (
                  <option key={v.id} value={v.id}>
                    {formatPlate(v.plate)} · {v.name}
                    {v.ownership === 'EMPLOYEE' ? ` (${v.ownerName})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Datum">
              <Input type="date" required value={tripDate} onChange={(e) => setTripDate(e.target.value)} />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
            <Field label="Řidič">
              <Input required list="trip-drivers" value={driverName} onChange={(e) => {
                  setDriverName(e.target.value)
                  setDriverTouched(true)
                }}
              />
            </Field>
            <Field label="Účel cesty">
              <Input
                required
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Schůzka s klientem, servis, nákup materiálu…"
              />
            </Field>
          </div>

          <div className="grid items-start gap-3 sm:grid-cols-[1fr_2fr]">
            <Field label="Tachometr na startu (km)">
              <Input
                inputMode="numeric"
                value={manualStart}
                onChange={(e) => setManualStart(e.target.value)}
                placeholder={hint?.expectedStart != null ? hint.expectedStart.toLocaleString('cs-CZ') : 'neznámý'}
              />
            </Field>
            <div className="pt-6 text-sm text-slate-600">
              {hint?.previous ? (
                <>
                  Předchozí jízda{' '}
                  <Link to={`/cesty/${hint.previous.id}`} className="text-brand-600 hover:underline">
                    {formatDate(hint.previous.tripDate)}
                  </Link>{' '}
                  ({hint.previous.route}) skončila na{' '}
                  <strong>
                    {hint.previous.odometerEnd !== null ? formatKm(hint.previous.odometerEnd) : 'neznámém stavu'}
                  </strong>
                  .
                </>
              ) : vehicle ? (
                <>
                  První jízda auta — navazuje na počáteční stav{' '}
                  <strong>{vehicle.initialOdometerKm !== null ? formatKm(vehicle.initialOdometerKm) : '(nevyplněný)'}</strong>.
                </>
              ) : null}
              {manualValue === null ? (
                <div className="text-xs text-slate-400">Nech prázdné, ať jízda naváže sama; vyplň jen při nesouladu s tachometrem.</div>
              ) : odometerGap !== 0 ? (
                <div className="text-xs text-amber-700">
                  Ručně {odometerGap > 0 ? `o ${formatKm(odometerGap)} víc` : `o ${formatKm(-odometerGap)} míň`} než
                  navazující stav — další jízdy auta navážou na tvůj stav.
                </div>
              ) : (
                <div className="text-xs text-slate-400">Shoduje se s navazujícím stavem.</div>
              )}
            </div>
          </div>

          <fieldset className="rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500">Trasa</legend>
            <ol className="space-y-2">
              {points.map((p, i) => (
                <li key={i} className="grid grid-cols-[1.5rem_1fr_6.5rem_1.5rem] items-center gap-2">
                  <span className="text-center text-xs text-slate-400">{i === 0 ? 'start' : i}</span>
                  <Input
                    required
                    list="trip-places"
                    value={p.place}
                    onChange={(e) => setPoint(i, { place: e.target.value })}
                    onBlur={() => fillKm(i)}
                    placeholder={i === 0 ? 'Odkud (např. Praha)' : 'Kam'}
                  />
                  {i === 0 ? (
                    <span />
                  ) : (
                    <div className="relative">
                      <Input
                        required
                        inputMode="numeric"
                        value={p.km}
                        onChange={(e) => setPoint(i, { km: e.target.value })}
                        className="pr-9 text-right"
                        aria-label={`km z ${points[i - 1].place || 'předchozího bodu'}`}
                      />
                      <span className="pointer-events-none absolute right-3 top-2 text-sm text-slate-400">km</span>
                    </div>
                  )}
                  {i > 1 ? (
                    <button
                      type="button"
                      onClick={() => removePoint(i)}
                      className="text-slate-400 hover:text-red-600"
                      aria-label="Odebrat bod"
                    >
                      ✕
                    </button>
                  ) : (
                    <span />
                  )}
                </li>
              ))}
            </ol>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <button type="button" className="text-brand-600 hover:underline" onClick={() => addPoint()}>
                + další bod
              </button>
              {start && last && start.toLowerCase() !== last.toLowerCase() && (
                <button type="button" className="text-brand-600 hover:underline" onClick={() => addPoint(start)}>
                  ↩ cesta zpět do {start}
                </button>
              )}
            </div>
          </fieldset>

          <fieldset className="rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500">Tankování</legend>
            <div className="grid items-start gap-3 sm:grid-cols-[1fr_2fr]">
              <Field label={`Cena paliva z dokladu (Kč/${fuelUnit})`}>
                <Input
                  inputMode="decimal"
                  value={fuelPrice}
                  onChange={(e) => setFuelPrice(e.target.value)}
                  placeholder={defaultFuelPrice ? formatNumber(defaultFuelPrice.value) : 'neuvedena'}
                />
              </Field>
              <div className="pt-6 text-xs text-slate-500">
                {fuelValue === null ? (
                  defaultFuelPrice ? (
                    <>
                      Bez dokladu se počítá s průměrnou cenou{' '}
                      <strong>
                        {formatNumber(defaultFuelPrice.value)} Kč/{fuelUnit}
                      </strong>{' '}
                      ({defaultFuelPrice.source}).
                    </>
                  ) : (
                    <span className="text-amber-700">Pro tohle palivo vyhláška průměrnou cenu nestanoví — zadej cenu z dokladu.</span>
                  )
                ) : (
                  <>
                    Náhrada za PHM se spočítá z ceny na dokladu
                    {defaultFuelPrice && ` místo průměrné ${formatNumber(defaultFuelPrice.value)} Kč/${fuelUnit}`}.
                    {(trip?.files.length ?? 0) + pending.length === 0 && (
                      <div className="text-amber-700">Přilož doklad o nákupu, ať je cena podložená.</div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="mt-3 space-y-1 text-sm">
              {trip?.files.map((f) => (
                <div key={f.id} className="flex items-center gap-3">
                  <a
                    href={tripsApi.fileUrl(f.id, true)}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate font-medium text-brand-700 hover:underline"
                  >
                    📎 {f.fileName}
                  </a>
                  <span className="text-xs text-slate-400">{formatBytes(f.sizeBytes)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    onBlur={() => setConfirmFile(null)}
                    className={`ml-auto text-xs hover:underline ${confirmFile === f.id ? 'font-semibold text-red-700' : 'text-red-500'}`}
                  >
                    {confirmFile === f.id ? 'opravdu smazat?' : 'smazat'}
                  </button>
                </div>
              ))}
              {pending.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex items-center gap-3">
                  <span className="truncate">📎 {f.name}</span>
                  <span className="text-xs text-slate-400">{formatBytes(f.size)} · nahraje se při uložení</span>
                  <button
                    type="button"
                    onClick={() => setPending(pending.filter((_, j) => j !== i))}
                    className="ml-auto text-xs text-slate-500 hover:underline"
                  >
                    odebrat
                  </button>
                </div>
              ))}
              <button type="button" className="text-brand-600 hover:underline" onClick={() => fileInput.current?.click()}>
                + přiložit doklad
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  setPending([...pending, ...Array.from(e.target.files ?? [])])
                  e.target.value = ''
                }}
              />
            </div>
          </fieldset>

          <Field label="Poznámka">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <Button type="submit" disabled={busy || !vehicleId} onClick={() => (keepGoing.current = false)}>
              {isNew ? 'Zapsat jízdu' : 'Uložit'}
            </Button>
            {isNew && (
              <Button
                type="submit"
                variant="secondary"
                disabled={busy || !vehicleId}
                onClick={() => (keepGoing.current = true)}
                title="Uloží jízdu a nechá formulář vyplněný s datem o den později"
              >
                Zapsat a další
              </Button>
            )}
            {!isNew && (
              <Button
                type="button"
                variant={confirmDelete ? 'danger' : 'ghost'}
                onClick={remove}
                onBlur={() => setConfirmDelete(false)}
              >
                {confirmDelete ? 'Opravdu smazat?' : 'Smazat'}
              </Button>
            )}
            {trip && (
              <span className="text-xs text-slate-400">
                Zapsal {trip.createdByEmail}, upraveno {formatDateTime(trip.updatedAt)}
              </span>
            )}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {saved.length > 0 && (
            <p className="text-sm text-green-700">
              Zapsáno: {saved.map(formatDate).join(', ')}.{' '}
              <Link to="/cesty" className="underline">
                Zpět na knihu jízd
              </Link>
            </p>
          )}
        </Card>

        <Card className="h-fit text-sm">
          <h2 className="mb-3 font-semibold">Souhrn</h2>
          <dl className="space-y-1">
            <Row label="Celkem" value={totalKm !== null ? formatKm(totalKm) : '—'} strong />
            <Row
              label="Tachometr"
              value={
                odometerStart !== null && totalKm !== null
                  ? `${odometerStart.toLocaleString('cs-CZ')} → ${(odometerStart + totalKm).toLocaleString('cs-CZ')}`
                  : '—'
              }
            />
            {vehicle && (
              <>
                <Row
                  label={fuelValue !== null && fuelValid ? 'Cena PHM (doklad)' : 'Cena PHM (vyhláška)'}
                  value={priceUsed !== null ? `${formatNumber(priceUsed)} Kč/${fuelUnit}` : '—'}
                />
                <Row
                  label={savedRates ? 'Sazba za km' : 'Sazba za km (dnes)'}
                  value={perKm !== null ? `${formatNumber(perKm)} Kč` : '—'}
                />
                <Row label={savedRates ? 'Náhrada' : 'Náhrada (odhad)'} value={formatCzk(preview)} strong />
              </>
            )}
          </dl>
          <p className="mt-3 text-xs text-slate-400">
            {odometerStart === null && vehicle
              ? 'Tachometr se nepočítá — u auta chybí počáteční stav tachometru.'
              : 'Tachometr navazuje na předchozí jízdu auta (nebo na ručně zadaný stav); zapíšeš-li starší jízdu, posunou se i ty pozdější.'}{' '}
            {vehicle?.ownership === 'COMPANY' && 'U firemního auta je náhrada jen za PHM, pokud ho řidič platil sám.'}
          </p>
        </Card>

        <datalist id="trip-places">
          {suggestions.places.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
        <datalist id="trip-drivers">
          {suggestions.drivers.map((d) => (
            <option key={d} value={d} />
          ))}
        </datalist>
      </form>
    </div>
  )
}

/** Sazba za km s danou cenou PHM — stejně jako backend (TravelRates.perKm). */
function perKmWith(rates: PerKmRate, ownership: VehicleOwnership, price: number | null): number | null {
  if (rates.consumption === null || price === null) return null
  // EPSILON kvůli plovoucí čárce: 8,195 se musí zaokrouhlit nahoru jako HALF_UP na backendu.
  const round = (n: number, places: number) => Math.round((n + Number.EPSILON) * 10 ** places) / 10 ** places
  const fuel = round((rates.consumption * price) / 100, 4)
  if (ownership === 'COMPANY') return round(fuel, 2)
  return rates.base ? round(rates.base.value + fuel, 2) : null
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${strong ? 'font-semibold' : ''}`}>
      <dt className={strong ? '' : 'text-slate-500'}>{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  )
}
